import React, { useState } from "react";
import { confirmResetPassword, resetPassword } from "aws-amplify/auth";
import { useTranslation } from "react-i18next";
import AuthPage from "./AuthPage.jsx";

export default function ForgotPasswordForm({ setShowForgotPassword }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const getErrorMessage = (authError) =>
    authError.message || t("unexpectedAuthError");

  const handleRequestCode = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const output = await resetPassword({
        username: email.trim().toLowerCase(),
      });
      if (
        output.nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE"
      ) {
        setStep(2);
      } else {
        setShowForgotPassword(false);
      }
    } catch (authError) {
      setError(getErrorMessage(authError));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmResetPassword = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      await confirmResetPassword({
        username: email.trim().toLowerCase(),
        confirmationCode: code,
        newPassword,
      });
      setShowForgotPassword(false);
    } catch (authError) {
      setError(getErrorMessage(authError));
    } finally {
      setLoading(false);
    }
  };

  const inputClassName =
    "mt-1 block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 shadow-sm outline-none transition focus:border-sky-600 focus:ring-2 focus:ring-sky-200";

  return (
    <AuthPage
      title={step === 1 ? t("forgotPasswordTitle") : t("resetPasswordTitle")}
      subtitle={
        step === 1 ? t("forgotPasswordSubtitle") : t("resetPasswordSubtitle")
      }
    >
      <form
        onSubmit={step === 1 ? handleRequestCode : handleConfirmResetPassword}
        className="space-y-5"
      >
        {step === 1 ? (
          <div>
            <label
              htmlFor="reset-email"
              className="text-sm font-medium text-slate-700"
            >
              {t("liEmail")}
            </label>
            <input
              id="reset-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className={inputClassName}
              disabled={loading}
              required
            />
          </div>
        ) : (
          <>
            <div>
              <label
                htmlFor="reset-code"
                className="text-sm font-medium text-slate-700"
              >
                {t("verificationCode")}
              </label>
              <input
                id="reset-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength="6"
                value={code}
                onChange={(event) =>
                  setCode(event.target.value.replace(/\s/g, ""))
                }
                className={inputClassName}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label
                htmlFor="reset-new-password"
                className="text-sm font-medium text-slate-700"
              >
                {t("newPassword")}
              </label>
              <input
                id="reset-new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className={inputClassName}
                disabled={loading}
                required
              />
            </div>
          </>
        )}

        {error && (
          <p
            role="alert"
            className="rounded-lg bg-red-50 p-3 text-sm text-red-700"
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-lg bg-sky-600 px-4 py-3 font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading
            ? t("processingRequest")
            : step === 1
              ? t("requestCode")
              : t("resetPassword")}
        </button>
        <button
          type="button"
          onClick={() => setShowForgotPassword(false)}
          disabled={loading}
          className="w-full text-sm font-medium text-sky-700 underline hover:text-sky-900"
        >
          {t("backToLogin")}
        </button>
      </form>
    </AuthPage>
  );
}
